import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Keep desktop carousel height in sync with the selected image's original ratio.
    if (this.presentation === 'carousel' && this.closest('.product-information')) {
      this.#imageRatioObserver = new MutationObserver(this.#syncImageRatio);
      this.#imageRatioObserver.observe(this, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['aria-hidden'],
      });
      this.addEventListener('load', this.#syncImageRatio, { capture: true, signal: this.#controller.signal });
      this.#syncImageRatio();
    }

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  /** @type {MutationObserver | undefined} */
  #imageRatioObserver;

  #syncImageRatio = () => {
    const scroller = this.refs.slideshow?.querySelector('slideshow-slides');
    const active = scroller?.querySelector(':scope > slideshow-slide[aria-hidden="false"]')
      || scroller?.querySelector(':scope > slideshow-slide');
    const image = active?.querySelector('img.product-media__image');
    const width = Number(image?.getAttribute('width'));
    const height = Number(image?.getAttribute('height'));
    const valid = width > 0 && height > 0;

    this.toggleAttribute('data-pq-adaptive-gallery', valid);
    if (valid) this.style.setProperty('--pq-gallery-ratio', String(width / height));
    else this.style.removeProperty('--pq-gallery-ratio');
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#imageRatioObserver?.disconnect();
    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
